import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../../context/AuthContext'
import {
  canManageStateRegulatory,
  getStateCoverageComparison,
  getRelationshipColor,
  getRelationshipLabel,
  exportComparisonToCsv,
} from '../../../lib/services/stateRegulatory'

const ALL_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
]

export function StateComparePage() {
  const { viewerContext } = useAuth()
  const [selected, setSelected] = useState<string[]>(['CA', 'TX', 'FL'])
  const canManage = canManageStateRegulatory(viewerContext)

  const { data: rows, isLoading } = useQuery({
    queryKey: ['state-compare', ...selected],
    queryFn: () => getStateCoverageComparison(selected),
    enabled: canManage && selected.length >= 2,
  })

  if (!canManage) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
        State regulatory comparison is restricted to superadmin role.
      </div>
    )
  }

  function toggleState(code: string) {
    setSelected((prev) =>
      prev.includes(code)
        ? prev.filter((c) => c !== code)
        : prev.length < 5
          ? [...prev, code]
          : prev
    )
  }

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <header className="v11-workspace-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">
              Multi-State CfC Comparison
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Select 2-5 states to compare coverage against CMS CfC sections
            </p>
          </div>
          {rows && rows.length > 0 && (
            <button
              className="btn-primary text-sm"
              onClick={() => exportComparisonToCsv(rows, selected)}
            >
              Export CSV
            </button>
          )}
        </div>
      </header>

      <section className="v11-section p-5">
        <div className="flex flex-wrap gap-1.5">
          {ALL_STATES.map((code) => (
            <button
              key={code}
              className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                selected.includes(code)
                  ? 'bg-[var(--brand-primary)] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              } ${selected.length >= 5 && !selected.includes(code) ? 'cursor-not-allowed opacity-50' : ''}`}
              onClick={() => toggleState(code)}
              disabled={selected.length >= 5 && !selected.includes(code)}
            >
              {code}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-gray-400">
          {selected.length}/5 selected: {selected.join(', ')}
        </p>
      </section>

      {selected.length < 2 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-900 text-sm">
          Select at least 2 states to see the comparison matrix.
        </div>
      )}

      {isLoading && (
        <div className="p-6 text-center text-gray-500">Loading comparison data...</div>
      )}

      {rows && rows.length > 0 && (
        <section className="v11-section overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600">CfC Section</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Name</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Domain</th>
                {selected.map((code) => (
                  <th key={code} className="px-3 py-2 text-center font-medium text-gray-600">
                    {code}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-3 py-2 font-medium">
                    {row.cfr_section}{row.cfr_subsection ?? ''}
                  </td>
                  <td className="max-w-xs truncate px-3 py-2 text-xs text-gray-600">
                    {row.cfr_name}
                  </td>
                  <td className="px-3 py-2 text-xs capitalize text-gray-500">
                    {(row.cfr_domain ?? '').replace('_', ' ')}
                  </td>
                  {selected.map((code) => {
                    const type = row.state_coverage[code] ?? 'no_data'
                    const colorClass = getRelationshipColor(type)
                    return (
                      <td key={code} className="px-3 py-2 text-center">
                        <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${colorClass}`}>
                          {getRelationshipLabel(type)}
                        </span>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  )
}
